import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import personImage from '../assets/person.png';
import profile from '../assets/profile.png';
import './Header.css';

function Header({ searchQuery, setSearchQuery }) {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState(searchQuery || ''); // Local state for input value
  const [mobileSearchActive, setMobileSearchActive] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Track mobile viewport
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle input change without navigating
  const handleInputChange = async (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Fetch autocomplete suggestions if the input has at least 2 characters
    if (value.length >= 2) {
      try {
        const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes/suggest?q=${encodeURIComponent(value.trim())}&visibility=true`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(Array.isArray(data) ? data : []);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle search when Enter is pressed
  const handleKeyPress = async (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      setSearchQuery(inputValue);
      setSuggestions([]);
      setShowSuggestions(false);
      
      try {
        const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes/search?q=${encodeURIComponent(inputValue.trim())}&visibility=true`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Navigate to the home page with search parameter
        navigate(`/?search=${encodeURIComponent(inputValue.trim())}`);
        
        if (isMobile) {
          setMobileSearchActive(false);
        }
      } catch (error) {
        console.error('Error searching recipes:', error);
      }
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setSearchQuery(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
    
    // Navigate to the home page with search parameter
    navigate(`/?search=${encodeURIComponent(suggestion)}`);
    
    if (isMobile) {
      setMobileSearchActive(false);
    }
  };

  // Toggle mobile search
  const toggleMobileSearch = () => {
    setMobileSearchActive(!mobileSearchActive);
  };

  // Hide suggestions when clicking outside the search area
  useEffect(() => {
    const handleClickOutside = () => {
      setShowSuggestions(false);
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <>
      <motion.div 
        className="header"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="header-left">
          <motion.div 
            className="logo-section"
            whileHover={{ scale: 1.02 }}
          >
            <a href="/">
            <span className="logo-text">Savory</span>
            </a>
            {isMobile ? (
              <Link to="/recipe-add" className="upload-button-mobile">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            ) : (
              <Link to="/recipe-add" className="upload-button">Add a recipe</Link>
            )}
          </motion.div>
        </div>

        <div className="header-right">
          {!isMobile && (
            <Link to="/advanced-search" className="advanced-search-button">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          )}

          {!isMobile && (
            <Link to="/creators" className="upload-button">Creators</Link>
          )}

          {isMobile && (
            <>
              <Link to="/advanced-search" className="upload-button-mobile">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <Link to="/creators" className="upload-button-mobile">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </>
          )}
          
          <Link to="/recipes-profile" className="profile-section">
            <motion.div 
              className="profile-info"
              whileHover={{ scale: 1.02 }}
            >
              <svg 
                width="26" 
                height="26" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className="about-us-icon"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" stroke="#2D3436" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 16V12" stroke="#2D3436" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 8H12.01" stroke="#2D3436" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.div>
          </Link>
        </div>
      </motion.div>

      {isMobile && (
        <motion.div 
          className={`mobile-search ${mobileSearchActive ? 'active' : ''}`}
          initial={{ y: -100 }}
          animate={{ y: mobileSearchActive ? 0 : -100 }}
          transition={{ duration: 0.3 }}
        >
          <button className="mobile-search-close" onClick={toggleMobileSearch}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="#2D3436" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="#2D3436" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="mobile-search-input-container">
            <input
              type="text"
              placeholder="Search recipes..."
              className="mobile-search-input"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            <button 
              className="mobile-search-button" 
              onClick={() => handleKeyPress({ key: 'Enter' })}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          
          {/* Mobile autocomplete suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="mobile-search-suggestions">
              {suggestions.map((suggestion, index) => (
                <div 
                  key={index} 
                  className="suggestion-item" 
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </>
  );
}

export default Header; 